import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Heart, Share2, Flag, MoreVertical, Pencil, Trash2, MapPin, Calendar, HelpCircle, CalendarDays, MessageSquare, Search, X, Tag, ArrowRight } from "lucide-react";
import api, { apiError, fileUrl, PLACEHOLDER_IMG } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ReportDialog, ShareDialog } from "@/components/dialogs";

const DISMISS_KEY = "mtaa_support_dismissed_at";
const SUPPORTED_KEY = "mtaa_supported_at";
const DISMISS_COOLDOWN = 7 * 24 * 3600 * 1000;   // 7 days after dismiss
const SUPPORTED_COOLDOWN = 30 * 24 * 3600 * 1000; // 30 days after contributing

export function supportCardEligible() {
  try {
    const s = +localStorage.getItem(SUPPORTED_KEY);
    if (s && Date.now() - s < SUPPORTED_COOLDOWN) return false;
    const d = +localStorage.getItem(DISMISS_KEY);
    if (d && Date.now() - d < DISMISS_COOLDOWN) return false;
  } catch { /* ignore */ }
  return true;
}

export function SupportCard({ onDismiss }) {
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    onDismiss && onDismiss();
  };
  return (
    <div className="relative rounded-2xl border border-border bg-card p-4" data-testid="home-support-card">
      <button onClick={dismiss} aria-label="Dismiss" data-testid="support-card-dismiss"
        className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"><X className="h-4 w-4" /></button>
      <div className="flex items-start gap-3 pr-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-red/10">
          <Heart className="h-5 w-5 text-brand-red" />
        </div>
        <div className="space-y-1">
          <h3 className="font-display font-semibold">Support Mtaa ❤️</h3>
          <p className="text-sm text-muted-foreground">Help us keep building Mtaa for the Kenyan community in Australia.</p>
          <Link to="/support" data-testid="support-card-cta" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-brand-green hover:underline">
            Support Mtaa <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export function DealsDiscoveryCard() {
  const [preview, setPreview] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/deals", { params: { limit: 3 } })
      .then(({ data }) => { if (alive) setPreview((data.items || []).slice(0, 3)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="home-deals-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Tag className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-display font-semibold">Mtaa Deals</h3>
            <p className="text-sm text-muted-foreground">Deals and useful finds from around the web.</p>
          </div>
        </div>
        <Link to="/deals" data-testid="home-deals-cta" className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary hover:underline">
          Explore Deals <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      {preview.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {preview.map((d) => (
            <Link key={d.id} to="/deals" data-testid={`home-deal-preview-${d.id}`} className="group overflow-hidden rounded-xl border border-border/60 bg-secondary/30">
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src={fileUrl(d.image_url) || PLACEHOLDER_IMG} alt={d.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <p className="line-clamp-1 px-2 py-1.5 text-[11px] font-medium text-muted-foreground">{d.title}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}



export const REACTIONS = [
  { key: "like", emoji: "👍", label: "Like" },
  { key: "love", emoji: "❤️", label: "Love" },
  { key: "haha", emoji: "😂", label: "Haha" },
  { key: "celebrate", emoji: "🎉", label: "Celebrate" },
  { key: "support", emoji: "🙏", label: "Support" },
];
const REMAP = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));
const TYPE_META = {
  post: { label: "Post", icon: MessageSquare, cls: "bg-secondary text-muted-foreground" },
  ask: { label: "Ask", icon: HelpCircle, cls: "bg-brand-green/10 text-brand-green" },
  event: { label: "Event", icon: CalendarDays, cls: "bg-brand-red/10 text-brand-red" },
  listing: { label: "For sale", icon: null, cls: "bg-brand-green/10 text-brand-green" },
};

export function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d`;
  return new Date(iso).toLocaleDateString("en-AU", { month: "short", day: "numeric" });
}

function TypeBadge({ kind }) {
  const m = TYPE_META[kind] || TYPE_META.post;
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${m.cls}`}>{Icon && <Icon className="h-3 w-3" />}{m.label}</span>;
}

export function CitySelect({ value, onChange, testid = "city-select" }) {
  const [q, setQ] = useState(value || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!q || q.trim().length < 2) { setOpts([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/meta/localities", { params: { q } })
        .then(({ data }) => setOpts(data.items))
        .catch(() => setOpts([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Search any Australian suburb, town or city" data-testid={testid}
          className="min-w-0 flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-muted-foreground" />
      </div>
      {open && q.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-border bg-popover shadow-lg" data-testid="city-options">
          {loading && <div className="px-3 py-2 text-xs text-muted-foreground" data-testid="city-loading">Searching…</div>}
          {!loading && opts.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground" data-testid="city-empty">No matching places</div>}
          {opts.map((o) => (
            <button key={`${o.locality}-${o.state}-${o.postcode}`} type="button"
              data-testid={`city-opt-${o.locality.toLowerCase().replace(/[^a-z]+/g, "-")}-${o.postcode}`}
              onClick={() => { onChange(o); setQ(`${o.locality}, ${o.state} ${o.postcode}`); setOpen(false); }}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-secondary">
              <span className="truncate">{o.locality}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{o.state} {o.postcode}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function LocationSettings() {
  const { user, refreshUser } = useAuth();
  const [sel, setSel] = useState(user?.city ? { locality: user.city, state: user.state, postcode: user.postcode } : null);
  const [saving, setSaving] = useState(false);
  const initial = user?.city ? `${user.city}${user.state ? `, ${user.state}` : ""}${user.postcode ? ` ${user.postcode}` : ""}` : "";
  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try { await api.put("/auth/profile", { city: sel.locality, state: sel.state, postcode: sel.postcode }); await refreshUser(); toast.success("Community location updated"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <section className="space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="location-section">
      <div><h2 className="font-display font-semibold">Community location</h2>
        <p className="text-sm text-muted-foreground">Your suburb or town decides which Mtaa community you see. Exact street address is never stored or shown.</p></div>
      <CitySelect value={initial} onChange={setSel} />
      <Button onClick={save} disabled={saving || !sel} data-testid="save-city">{saving ? "Saving…" : "Save location"}</Button>
    </section>
  );
}

export function LocationPickerDialog({ open, onOpenChange, onSaved }) {
  const { user, refreshUser } = useAuth();
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const initial = user?.city ? `${user.city}${user.state ? `, ${user.state}` : ""}${user.postcode ? ` ${user.postcode}` : ""}` : "";
  useEffect(() => { if (open) setSel(null); }, [open]);
  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      await api.put("/auth/profile", { city: sel.locality, state: sel.state, postcode: sel.postcode, country: "Australia" });
      await refreshUser();
      toast.success("Community location updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="location-picker-modal">
        <DialogHeader><DialogTitle>Your Mtaa community</DialogTitle>
          <DialogDescription>Search any Australian suburb, town or city. Your exact street address is never stored or shown.</DialogDescription>
        </DialogHeader>
        <CitySelect value={initial} onChange={setSel} testid="home-city-select" />
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1" onClick={save} disabled={saving || !sel} data-testid="home-save-city">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreatePostDialog({ open, onOpenChange, type = "post", editing = null, onDone }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const isEdit = !!editing;
  const t = isEdit ? editing.kind : type;
  const [text, setText] = useState("");
  const [ev, setEv] = useState({ title: "", starts_at: "", venue: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setText(editing?.text || "");
      setEv(editing?.event ? { title: editing.event.title || "", starts_at: editing.event.starts_at || "", venue: editing.event.venue || "" } : { title: "", starts_at: "", venue: "" });
    }
  }, [open, editing]);
  const placeholder = t === "ask" ? "Ask your Mtaa… e.g. Anyone know a good Kenyan barber in Perth?" : t === "event" ? "Describe your event…" : "Share something with your Mtaa…";
  const submit = async () => {
    if (!text.trim()) return toast.error("Please write something");
    if (t === "event" && !ev.title.trim()) return toast.error("Event needs a title");
    setBusy(true);
    try {
      const body = { type: t, text, event_title: ev.title, event_starts_at: ev.starts_at, event_venue: ev.venue };
      const { data } = isEdit ? await api.put(`/posts/${editing.id}`, body) : await api.post("/posts", body);
      toast.success(isEdit ? "Updated" : "Posted to your Mtaa");
      onOpenChange(false);
      onDone && onDone(data, isEdit);
      if (!isEdit && data?.id) nav(`/p/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const titleMap = { post: "Create post", ask: "Ask Mtaa", event: "Create event" };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-post-modal">
        <DialogHeader><DialogTitle>{isEdit ? "Edit" : titleMap[t] || "Create post"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {t === "event" && (
            <>
              <div><Label>Event title</Label><Input value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} data-testid="event-title" /></div>
              <div className="flex gap-2">
                <div className="flex-1"><Label>Date & time</Label><Input type="datetime-local" value={ev.starts_at} onChange={(e) => setEv({ ...ev, starts_at: e.target.value })} data-testid="event-date" /></div>
                <div className="flex-1"><Label>Venue</Label><Input value={ev.venue} onChange={(e) => setEv({ ...ev, venue: e.target.value })} placeholder="Venue / area" data-testid="event-venue" /></div>
              </div>
            </>
          )}
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} rows={4} data-testid="post-text" />
          <p className="text-xs text-muted-foreground">Posting to {user?.city || user?.state || "Australia"} community.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="post-cancel">Cancel</Button>
          <Button className="flex-1" onClick={submit} disabled={busy} data-testid="post-submit">{busy ? "Posting…" : isEdit ? "Save" : "Post"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReactionBar({ post, onChanged }) {
  const { user } = useAuth();
  const gate = useAuthGate();
  const nav = useNavigate();
  const [picker, setPicker] = useState(false);
  const [mine, setMine] = useState(post.my_reaction);
  const [total, setTotal] = useState(post.reaction_total || 0);
  useEffect(() => { setMine(post.my_reaction); setTotal(post.reaction_total || 0); }, [post.my_reaction, post.reaction_total]);
  const doReact = async (key) => {
    if (!gate.require("Create a free account to react and join the conversation.")) return;
    setPicker(false);
    const prev = mine;
    try {
      const { data } = await api.post(`/posts/${post.id}/react`, { type: key });
      setMine(data.my_reaction);
      setTotal((t) => t + (data.my_reaction ? (prev ? 0 : 1) : -1));
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cur = mine ? REMAP[mine] : null;
  return (
    <div className="relative">
      {picker && (
        <div className="absolute -top-11 left-0 z-20 flex gap-1 rounded-full border border-border bg-popover px-2 py-1 shadow-lg" data-testid={`reaction-picker-${post.id}`}>
          {REACTIONS.map((r) => <button key={r.key} onClick={() => doReact(r.key)} className="text-xl transition-transform hover:scale-125" data-testid={`react-${r.key}-${post.id}`}>{r.emoji}</button>)}
        </div>
      )}
      <button onClick={() => (mine ? doReact(mine) : setPicker((p) => !p))} onContextMenu={(e) => { e.preventDefault(); setPicker(true); }}
        className={`flex items-center gap-1.5 text-sm font-medium ${cur ? "text-brand-red" : "text-muted-foreground"}`} data-testid={`reaction-btn-${post.id}`}>
        <span className="text-base">{cur ? cur.emoji : "👍"}</span>{cur ? cur.label : "React"}{total > 0 && <span className="text-xs">· {total}</span>}
      </button>
    </div>
  );
}

export function PostCard({ item, onChanged, onDeleted }) {
  const { user } = useAuth();
  const gate = useAuthGate();
  const nav = useNavigate();
  const [report, setReport] = useState(false);
  const [share, setShare] = useState(false);
  const [edit, setEdit] = useState(false);
  const [saved, setSaved] = useState(false);
  const [interested, setInterested] = useState(item.interested);
  const [intCount, setIntCount] = useState(item.interested_count || 0);
  const isOwner = user && item.owner_id === user.id;
  const a = item.author || {};
  const save = async () => {
    if (!gate.require("Create a free account to save posts and connect with the community.")) return;
    try {
      if (saved) { await api.delete("/favourites", { params: { item_type: "post", item_id: item.id } }); setSaved(false); }
      else { await api.post("/favourites", { item_type: "post", item_id: item.id }); setSaved(true); toast.success("Saved"); }
    } catch (e) { toast.error(apiError(e)); }
  };
  const rsvp = async () => {
    if (!gate.require("Create a free account to RSVP to community events.")) return;
    try { const { data } = await api.post(`/posts/${item.id}/interested`); setInterested(data.interested); setIntCount((c) => c + (data.interested ? 1 : -1)); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => { if (!window.confirm("Delete this post? This cannot be undone.")) return; try { await api.delete(`/posts/${item.id}`); toast.success("Post deleted"); onDeleted ? onDeleted(item.id) : onChanged && onChanged(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <article className="rounded-2xl border border-border bg-card p-4" data-testid={`post-card-${item.id}`}>
      <div className="flex items-start gap-3">
        <Link to={`/u/${a.id}`} className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary font-semibold text-primary">
          {a.avatar_url ? <img src={fileUrl(a.avatar_url)} alt="" className="h-full w-full object-cover" /> : (a.name || "U")[0]?.toUpperCase()}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link to={`/u/${a.id}`} className="truncate text-sm font-semibold hover:underline">{a.name || "Community member"}</Link>
            <TypeBadge kind={item.kind} />
          </div>
          <p className="text-xs text-muted-foreground"><MapPin className="mr-0.5 inline h-3 w-3" />{item.city ? `${item.city}${item.state ? `, ${item.state}` : ""}` : item.state || "Australia"} · {timeAgo(item.created_at)}</p>
        </div>
        {isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><button className="rounded-lg p-1 text-muted-foreground hover:bg-secondary" data-testid={`post-menu-${item.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEdit(true)} data-testid={`post-edit-${item.id}`}><Pencil className="h-4 w-4" />Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={del} className="text-destructive focus:text-destructive" data-testid={`post-delete-${item.id}`}><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {item.kind === "event" && item.event?.title && (
        <div className="mt-3 rounded-xl border border-border bg-secondary/40 p-3">
          <p className="font-display font-bold">{item.event.title}</p>
          {item.event.starts_at && <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="h-3 w-3" />{new Date(item.event.starts_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</p>}
          {item.event.venue && <p className="text-xs text-muted-foreground"><MapPin className="mr-0.5 inline h-3 w-3" />{item.event.venue}</p>}
        </div>
      )}
      <p className="mt-2.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{item.text}</p>
      {item.photos?.length > 0 && <img src={fileUrl(item.photos[0])} alt="" className="mt-3 max-h-80 w-full rounded-xl object-cover" />}
      {item.kind === "event" && (
        <Button variant={interested ? "secondary" : "outline"} size="sm" className="mt-3" onClick={rsvp} data-testid={`event-interested-${item.id}`}>
          🙋 Interested{intCount > 0 && ` · ${intCount}`}
        </Button>
      )}
      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        <ReactionBar post={item} onChanged={onChanged} />
        <Link to={`/p/${item.id}`} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground" data-testid={`post-comments-${item.id}`}><MessageCircle className="h-4 w-4" />{item.comment_count || 0}</Link>
        <button onClick={save} className={`flex items-center gap-1.5 text-sm font-medium ${saved ? "text-brand-red" : "text-muted-foreground"}`} data-testid={`post-save-${item.id}`}><Heart className={saved ? "h-4 w-4 fill-brand-red" : "h-4 w-4"} />Save</button>
        <button onClick={() => setShare(true)} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground" data-testid={`post-share-${item.id}`}><Share2 className="h-4 w-4" /></button>
        {!isOwner && <button onClick={() => (user ? setReport(true) : gate.require("Create a free account to report content."))} className="text-sm text-muted-foreground" data-testid={`post-report-${item.id}`}><Flag className="h-4 w-4" /></button>}
      </div>
      <ReportDialog open={report} onOpenChange={setReport} targetType="post" targetId={item.id} reportedUserId={item.owner_id} reportedUserName={a.name} onBlocked={() => onChanged && onChanged()} />
      <ShareDialog open={share} onOpenChange={setShare} url={`/p/${item.id}`} title={item.text?.slice(0, 60) || "Mtaa post"} />
      <CreatePostDialog open={edit} onOpenChange={setEdit} editing={item} onDone={() => onChanged && onChanged()} />
    </article>
  );
}

export function MarketplaceFeedCard({ item }) {
  return (
    <Link to={`/listing/${item.id}`} data-testid={`feed-listing-${item.id}`} className="block overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center gap-2 px-4 pt-3"><TypeBadge kind="listing" />{item.featured_until && item.featured_until > new Date().toISOString() && <span className="inline-flex items-center rounded-md border border-brand-green/40 bg-brand-green/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-green" data-testid="feed-featured-tag">Featured</span>}<span className="text-xs text-muted-foreground">· {timeAgo(item.created_at)}</span></div>
      <div className="relative mt-2 aspect-[16/10] w-full overflow-hidden bg-muted">
        <img src={fileUrl(item.photos?.[0]) || PLACEHOLDER_IMG} alt={item.title} className="h-full w-full object-cover" />
        <span className="absolute bottom-2 left-2 rounded-lg bg-brand-green px-2.5 py-1 text-sm font-bold text-white">{item.price > 0 ? `$${item.price.toLocaleString()}` : "Free"}</span>
      </div>
      <div className="p-3">
        <h3 className="line-clamp-1 text-sm font-semibold">{item.title}</h3>
        <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{item.city ? `${item.city}, ` : ""}{item.state}{item.condition ? ` · ${item.condition}` : ""}</p>
      </div>
    </Link>
  );
}

export function CreateMenu({ children }) {
  // Renders a trigger + dropdown with the 4 create actions, and hosts the composer.
  const nav = useNavigate();
  const gate = useAuthGate();
  const [type, setType] = useState(null);
  const open = (t) => { if (!gate.require("Create a free account to post, ask, sell or add events on Mtaa.")) return; if (t === "sell") nav("/post/sell"); else setType(t); };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-56">
          <DropdownMenuItem onClick={() => open("post")} data-testid="create-post"><MessageSquare className="h-4 w-4" />Create post</DropdownMenuItem>
          <DropdownMenuItem onClick={() => open("ask")} data-testid="create-ask"><HelpCircle className="h-4 w-4" />Ask Mtaa</DropdownMenuItem>
          <DropdownMenuItem onClick={() => open("sell")} data-testid="create-sell"><span className="text-base leading-none">🏷️</span>Sell something</DropdownMenuItem>
          <DropdownMenuItem onClick={() => open("event")} data-testid="create-event"><CalendarDays className="h-4 w-4" />Create event</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreatePostDialog open={!!type} onOpenChange={(o) => !o && setType(null)} type={type || "post"} />
    </>
  );
}
